/**
 * PressureWashingDashboard — the owner-facing hub for pressure washing
 * businesses. Renders category-branded content for every sidebar tab so the
 * whole experience feels bespoke (not the generic mechanic dashboard).
 *
 * Tab routing is driven by `?tab=` query param (matches Grocery/Wholesale
 * patterns). Current tabs: dashboard (default), quotes, jobs, customers,
 * rates, invoices. All widgets are mock-data placeholders until the real
 * map-based quote CRUD ships.
 */
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { useTheme } from "@/context/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  MapPin, Droplets, ClipboardList, Users, DollarSign, FileText,
  Plus, Sparkles, Send, Home, ChevronRight, TrendingUp, Calendar, Zap,
} from "lucide-react";
import PressureWashingDemo from "@/components/PressureWashingDemo";

const TAB_META = {
  dashboard: { icon: Sparkles, label: "Dashboard",       blurb: "Instant aerial quote tool + revenue snapshot." },
  quotes:    { icon: MapPin,   label: "Aerial Quotes",   blurb: "Drop polygons on satellite imagery to auto-calc sqft." },
  jobs:      { icon: ClipboardList, label: "Jobs",       blurb: "Scheduled cleans, in-progress, and completed." },
  customers: { icon: Users,    label: "Customers",       blurb: "Homeowners, HOAs, and commercial accounts." },
  rates:     { icon: Droplets, label: "Surface Rates",   blurb: "Your per-sqft pricing per surface type." },
  invoices:  { icon: FileText, label: "Invoices",        blurb: "Accepted quotes converted to invoices." },
};

// -----------------------------------------------------------
// Mock data (until real backend ships)
// -----------------------------------------------------------
const SAMPLE_QUOTES = [
  { id: "Q-00214", customer: "Michelle Turner",   address: "412 Oak Ridge Dr",   total: 458.60,  status: "sent",      when: "Today" },
  { id: "Q-00213", customer: "Anthony Rivera",    address: "88 Lakeview Ln",     total: 1284.00, status: "accepted",  when: "Yesterday" },
  { id: "Q-00212", customer: "Green Valley HOA",  address: "1200 Clubhouse Rd",  total: 3420.75, status: "converted", when: "2 days ago" },
  { id: "Q-00211", customer: "Dr. Priya Sharma",  address: "27 Magnolia Ct",     total: 612.30,  status: "draft",     when: "3 days ago" },
];

const SAMPLE_JOBS = [
  { id: "J-1042", customer: "Anthony Rivera",   address: "88 Lakeview Ln",     when: "Tomorrow 9:00 AM",  crew: "Ricky & Marcus", status: "scheduled" },
  { id: "J-1041", customer: "Green Valley HOA", address: "1200 Clubhouse Rd",  when: "Today 2:00 PM",     crew: "Ricky & Sam",    status: "in_progress" },
  { id: "J-1040", customer: "Peter O'Malley",   address: "77 Elm St",          when: "Yesterday",         crew: "Marcus",          status: "completed" },
];

const SAMPLE_CUSTOMERS = [
  { name: "Green Valley HOA",       type: "Commercial", address: "1200 Clubhouse Rd", lifetime: 8420.50, jobs: 6 },
  { name: "Anthony Rivera",         type: "Residential", address: "88 Lakeview Ln",   lifetime: 1284.00, jobs: 1 },
  { name: "Michelle Turner",        type: "Residential", address: "412 Oak Ridge Dr", lifetime: 458.60,  jobs: 1 },
  { name: "Dr. Priya Sharma",       type: "Residential", address: "27 Magnolia Ct",   lifetime: 612.30,  jobs: 1 },
];

const SAMPLE_RATES = [
  { key: "driveway",     label: "Driveway",         rate: 0.15, note: "Concrete or asphalt" },
  { key: "front_walk",   label: "Front Walkway",    rate: 0.20, note: "Includes stairs + curb" },
  { key: "back_patio",   label: "Back Patio",       rate: 0.18, note: "Concrete or pavers" },
  { key: "house_siding", label: "House Siding",     rate: 0.30, note: "Vinyl, brick, stucco" },
  { key: "roof",         label: "Roof (soft wash)", rate: 0.40, note: "Asphalt or metal" },
  { key: "fence",        label: "Fence",            rate: 0.25, note: "Wood or vinyl" },
];

const SAMPLE_INVOICES = [
  { id: "INV-00089", customer: "Green Valley HOA",    total: 3420.75, paid: true,  when: "3 days ago", method: "Stripe" },
  { id: "INV-00088", customer: "Anthony Rivera",      total: 1284.00, paid: false, when: "Yesterday",  method: "Sent" },
  { id: "INV-00087", customer: "Michelle Turner",     total: 458.60,  paid: false, when: "Today",      method: "Sent" },
];

const STATUS_STYLES = {
  draft:       { bg: "bg-slate-200",   text: "text-slate-700" },
  sent:        { bg: "bg-sky-100",     text: "text-sky-700"    },
  accepted:    { bg: "bg-emerald-100", text: "text-emerald-700" },
  converted:   { bg: "bg-violet-100",  text: "text-violet-700" },
  scheduled:   { bg: "bg-sky-100",     text: "text-sky-700"     },
  in_progress: { bg: "bg-amber-100",   text: "text-amber-700"   },
  completed:   { bg: "bg-emerald-100", text: "text-emerald-700" },
};

// -----------------------------------------------------------
// Tab bodies
// -----------------------------------------------------------
const QuotesTab = ({ isDark }) => {
  const cardBg = isDark ? "bg-slate-800 border-slate-700" : "";
  const sub = isDark ? "text-slate-400" : "text-slate-500";
  const heading = isDark ? "text-white" : "text-slate-800";
  return (
    <div className="space-y-4">
      <Card className={cardBg}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className={`flex items-center gap-2 ${heading}`}>
            <MapPin className="w-5 h-5 text-cyan-500" /> All Quotes
          </CardTitle>
          <Button className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="new-quote-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Quote
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-500/20">
            {SAMPLE_QUOTES.map((q) => {
              const style = STATUS_STYLES[q.status];
              return (
                <div key={q.id} className="flex items-center gap-3 px-4 py-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? "bg-slate-700" : "bg-slate-100"}`}>
                    <Home className={`w-4 h-4 ${sub}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${heading}`}>{q.customer}</p>
                    <p className={`text-xs truncate ${sub}`}>{q.id} · {q.address} · {q.when}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-semibold ${heading}`}>${q.total.toFixed(2)}</p>
                    <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${style.bg} ${style.text} uppercase tracking-wide`}>
                      {q.status}
                    </span>
                  </div>
                  <ChevronRight className={`w-4 h-4 ${sub}`} />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const JobsTab = ({ isDark }) => {
  const cardBg = isDark ? "bg-slate-800 border-slate-700" : "";
  const sub = isDark ? "text-slate-400" : "text-slate-500";
  const heading = isDark ? "text-white" : "text-slate-800";
  return (
    <Card className={cardBg}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className={`flex items-center gap-2 ${heading}`}>
          <ClipboardList className="w-5 h-5 text-cyan-500" /> Scheduled + Active Jobs
        </CardTitle>
        <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-100">
          {SAMPLE_JOBS.filter((j) => j.status !== 'completed').length} upcoming
        </Badge>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-slate-500/20">
          {SAMPLE_JOBS.map((j) => {
            const style = STATUS_STYLES[j.status];
            return (
              <div key={j.id} className="flex items-center gap-3 px-4 py-3">
                <Calendar className={`w-5 h-5 ${sub}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${heading}`}>{j.customer}</p>
                  <p className={`text-xs truncate ${sub}`}>{j.id} · {j.address} · Crew: {j.crew}</p>
                </div>
                <div className="text-right">
                  <p className={`text-sm ${heading}`}>{j.when}</p>
                  <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${style.bg} ${style.text} uppercase tracking-wide`}>
                    {j.status.replace("_", " ")}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const CustomersTab = ({ isDark }) => {
  const cardBg = isDark ? "bg-slate-800 border-slate-700" : "";
  const sub = isDark ? "text-slate-400" : "text-slate-500";
  const heading = isDark ? "text-white" : "text-slate-800";
  return (
    <Card className={cardBg}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className={`flex items-center gap-2 ${heading}`}>
          <Users className="w-5 h-5 text-cyan-500" /> Customers
        </CardTitle>
        <Button className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="new-customer-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add Customer
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-slate-500/20">
          {SAMPLE_CUSTOMERS.map((c) => (
            <div key={c.name} className="flex items-center gap-3 px-4 py-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${isDark ? "bg-slate-700 text-slate-200" : "bg-slate-100 text-slate-700"}`}>
                {c.name.slice(0, 1)}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${heading}`}>
                  {c.name}
                  <Badge className="ml-2 bg-cyan-100 text-cyan-700 border border-cyan-300 hover:bg-cyan-100 text-[10px] px-1.5 py-0 align-middle">
                    {c.type}
                  </Badge>
                </p>
                <p className={`text-xs truncate ${sub}`}>{c.address} · {c.jobs} job{c.jobs === 1 ? "" : "s"} completed</p>
              </div>
              <p className={`text-sm font-semibold ${heading}`}>${c.lifetime.toFixed(2)}</p>
              <ChevronRight className={`w-4 h-4 ${sub}`} />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const RatesTab = ({ isDark }) => {
  const cardBg = isDark ? "bg-slate-800 border-slate-700" : "";
  const sub = isDark ? "text-slate-400" : "text-slate-500";
  const heading = isDark ? "text-white" : "text-slate-800";
  return (
    <Card className={cardBg}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className={`flex items-center gap-2 ${heading}`}>
          <Droplets className="w-5 h-5 text-cyan-500" /> Per-Surface Rates
        </CardTitle>
        <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-100">
          $150 min. job
        </Badge>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-slate-500/20">
          {SAMPLE_RATES.map((r) => (
            <div key={r.key} className="flex items-center gap-3 px-4 py-3">
              <div className="w-2 h-8 rounded-sm bg-cyan-500" />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${heading}`}>{r.label}</p>
                <p className={`text-xs ${sub}`}>{r.note}</p>
              </div>
              <p className={`text-base font-mono font-semibold ${heading}`}>${r.rate.toFixed(2)}<span className={`text-xs font-normal ml-1 ${sub}`}>/sqft</span></p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const InvoicesTab = ({ isDark }) => {
  const cardBg = isDark ? "bg-slate-800 border-slate-700" : "";
  const sub = isDark ? "text-slate-400" : "text-slate-500";
  const heading = isDark ? "text-white" : "text-slate-800";
  return (
    <Card className={cardBg}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className={`flex items-center gap-2 ${heading}`}>
          <FileText className="w-5 h-5 text-cyan-500" /> Invoices
        </CardTitle>
        <div className="text-sm font-medium">
          <span className={sub}>Outstanding: </span>
          <span className="text-amber-500 font-bold">
            ${SAMPLE_INVOICES.filter((i) => !i.paid).reduce((s, i) => s + i.total, 0).toFixed(2)}
          </span>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-slate-500/20">
          {SAMPLE_INVOICES.map((inv) => (
            <div key={inv.id} className="flex items-center gap-3 px-4 py-3">
              <FileText className={`w-5 h-5 ${sub}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${heading}`}>{inv.customer}</p>
                <p className={`text-xs truncate ${sub}`}>{inv.id} · {inv.when} · via {inv.method}</p>
              </div>
              <p className={`text-sm font-semibold ${heading}`}>${inv.total.toFixed(2)}</p>
              <Badge className={inv.paid
                ? "bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-100"
                : "bg-amber-100 text-amber-700 border border-amber-300 hover:bg-amber-100"}>
                {inv.paid ? "PAID" : "OUTSTANDING"}
              </Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// -----------------------------------------------------------
// Main
// -----------------------------------------------------------
export default function PressureWashingDashboard() {
  const { isDark } = useTheme();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "dashboard";
  const meta = TAB_META[tab] || TAB_META.dashboard;
  const HeaderIcon = meta.icon;
  const heading = isDark ? "text-white" : "text-slate-800";
  const sub = isDark ? "text-slate-400" : "text-slate-500";

  const renderTab = () => {
    switch (tab) {
      case "quotes":    return <QuotesTab isDark={isDark} />;
      case "jobs":      return <JobsTab isDark={isDark} />;
      case "customers": return <CustomersTab isDark={isDark} />;
      case "rates":     return <RatesTab isDark={isDark} />;
      case "invoices":  return <InvoicesTab isDark={isDark} />;
      default:          return <PressureWashingDemo isDark={isDark} />;
    }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="pressure-washing-dashboard">
        <div className="mb-6 flex items-center gap-3 flex-wrap">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/15 flex items-center justify-center">
            <HeaderIcon className="w-5 h-5 text-cyan-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className={`text-2xl sm:text-3xl font-bold ${heading}`}>{meta.label}</h1>
            <p className={`text-sm mt-0.5 ${sub}`}>{meta.blurb}</p>
          </div>
          <Badge className="bg-cyan-100 text-cyan-700 border border-cyan-300 hover:bg-cyan-100">
            <Droplets className="w-3 h-3 mr-1" /> Pressure Washing
          </Badge>
        </div>
        {renderTab()}
      </div>
    </Layout>
  );
}
